import { useRef, useState } from "react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { useDeleteProVenueInstance } from "@/hooks/use-provenue-instances"
import type { SharedProVenueInstance } from "@/types/provenue-instance"

interface DeleteProVenueInstanceDialogProps {
  instance: SharedProVenueInstance
  onClose: () => void
  onDeleted: (instance: SharedProVenueInstance) => Promise<void>
}

export function DeleteProVenueInstanceDialog({ instance, onClose, onDeleted }: DeleteProVenueInstanceDialogProps) {
  const deleteInstance = useDeleteProVenueInstance()
  const submitting = useRef(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string>()
  const confirm = async () => {
    if (submitting.current) return
    submitting.current = true
    setIsDeleting(true)
    setError(undefined)
    try {
      await deleteInstance.trigger(instance.id)
      await onDeleted(instance)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to delete instance. Please try again.")
    } finally {
      submitting.current = false
      setIsDeleting(false)
    }
  }

  return (
    <AlertDialog open onOpenChange={(open) => { if (!open && !submitting.current) onClose() }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete ProVenue instance?</AlertDialogTitle>
          <AlertDialogDescription>This will permanently delete {instance.name} (ID {instance.id}). This action cannot be undone. Instances used by a device or global configuration cannot be deleted.</AlertDialogDescription>
        </AlertDialogHeader>
        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={isDeleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={(event) => { event.preventDefault(); void confirm() }}>
            {isDeleting ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
